import { useRedirectFunctions } from "@propelauth/react"
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar"
import { LoadingSpinner } from "components/ui/loading-spinner"
import { ChevronDown, PanelLeft, PanelLeftClose, Plus, Users } from "lucide-react"
import React, { ReactNode, Suspense, useCallback, useEffect, useState } from "react"
import { graphql, useLazyLoadQuery } from "react-relay"
import { useLocation, useNavigate } from "react-router"
import type { MeUserDataType } from "src/app"
import { cn } from "src/common/utils"
import { ScopeGate } from "src/components/ScopeGate"
import ProductLogo from "src/components/ui/icons/ProductLogo"
import { useNavigation } from "src/context/NavigationContext"
import { useTeamSelection } from "src/hooks/use-team-selection"
import { getKnowledgeBasePath, isSlackManagedByRootly } from "src/utils/brandTheme"

import type { headerTeamsQuery } from "./__generated__/headerTeamsQuery.graphql"

interface UserAvatarProps {
  pictureUrl: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  isLoading?: boolean;
}

const UserAvatar = React.memo<UserAvatarProps>(function UserAvatar({
  pictureUrl,
  firstName,
  lastName,
  isLoading = false,
}) {
  return (
    <div className="relative">
      <Avatar
        className={cn(
          "h-9 w-9 text-muted-foreground ring-1 ring-border transition-colors duration-200",
          "hover:ring-foreground/30 hover:ring-1",
          isLoading && "opacity-60",
        )}
      >
        <AvatarImage
          src={pictureUrl || undefined}
          alt={`${firstName} ${lastName}`}
        />
        <AvatarFallback className="bg-muted text-foreground font-medium transition-colors duration-200 hover:bg-accent">
          {`${firstName?.[0] || ""}${lastName?.[0] || ""}`}
        </AvatarFallback>
      </Avatar>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner className="text-foreground h-5 w-5" />
        </div>
      )}
    </div>
  );
});

const teamsQuery = graphql`
  query headerTeamsQuery($organizationId: GlobalID!) {
    node(id: $organizationId) {
      ... on GraphQLOrganization {
        teams {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`

// Component to handle loading teams data
function TeamsList({
  organizationId,
  organizationName,
  selectedTeamId,
  onSelectTeam,
  onClearSelection,
  isSuperAdmin,
}: {
  organizationId: string,
  organizationName: string,
  selectedTeamId: string | null,
  onSelectTeam: (id: string, name: string) => void,
  onClearSelection: () => void,
  isSuperAdmin: boolean,
}) {
  // Query teams for the organization
  const data = useLazyLoadQuery<headerTeamsQuery>(
    teamsQuery,
    { organizationId }
  );

  // Extract teams from query response
  const teamsUnsorted = data.node?.teams?.edges || [];

  // Sort teams alphabetically by name
  const teams = [...teamsUnsorted].sort((a, b) =>
    a.node.name.localeCompare(b.node.name)
  );

  return (
    <div className="py-1.5">
      {/* Organization Section */}
      <div className="px-3 pb-1.5 border-b border-border">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wide">Organization</span>
          {isSuperAdmin && (
            <button
              className="text-[11px] text-red-400 hover:text-red-300"
              onClick={onClearSelection}
            >
              [debug] clear
            </button>
          )}
        </div>
        <div className="text-[13px] text-foreground mt-0.5">{organizationName}</div>
      </div>

      {/* Teams Section */}
      <div className="pt-1.5">
        <div className="px-3 pb-0.5">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wide">Teams</span>
        </div>

        {teams.length === 0 ? (
          <div className="px-3 py-1 text-[13px] text-muted-foreground italic">
            No teams found
          </div>
        ) : (
          <div className="max-h-40 overflow-y-auto custom-scrollbar">
            {teams.map((edge) => (
              <div
                key={edge.node.id}
                className={cn(
                  "px-3 py-1 cursor-pointer hover:bg-accent flex items-center gap-2 text-[13px] transition-colors",
                  selectedTeamId === edge.node.id ? "text-primary" : "text-foreground"
                )}
                onClick={() => onSelectTeam(edge.node.id, edge.node.name)}
              >
                <span className={cn(
                  "w-1.5 h-1.5 rounded-full flex-shrink-0",
                  selectedTeamId === edge.node.id ? "bg-primary" : "bg-transparent"
                )} />
                {edge.node.name}
              </div>
            ))}
          </div>
        )}

        {/* Create New Team */}
        <ScopeGate scope="write:teams">
          <div className="border-t border-border mt-1.5 pt-1.5">
            <div
              className="px-3 py-1 cursor-pointer hover:bg-accent text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-[13px] transition-colors"
              onClick={() => window.location.href = "/settings/teams/new"}
            >
              <Plus size={14} />
              Create New Team
            </div>
            <div
                className="px-3 py-1 cursor-pointer hover:bg-accent text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-[13px] transition-colors"
                onClick={() => window.location.href = "/settings/teams"}
              >
                <Users size={14} />
                Manage Teams
              </div>
          </div>
        </ScopeGate>
      </div>
    </div>
  );
}

interface IProps {
  leftNode?: ReactNode
  me: MeUserDataType
}


// Logo title component - not used currently
// function LogoTitle() {
//   return (
//     <div className="flex w-full content-center justify-center self-stretch">
//       <div className="h-12 w-12">
//         <TierZeroIcon />
//       </div>
//     </div>
//   )
// }

export function Header(props: IProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { redirectToAccountPage } = useRedirectFunctions();
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDemoApp, setIsDemoApp] = useState(false);
  const { toggleLeftNav, isLeftNavCollapsed } = useNavigation();
  const brandTheme = props.me.organization?.brandTheme ?? "TIERZERO";
  const isRootlyManaged = isSlackManagedByRootly(brandTheme);
  const knowledgeBasePath = getKnowledgeBasePath(brandTheme);

  const isSuperAdmin = props.me.isSuperAdmin;
  const organizationId = props.me.organization?.id;
  const userId = props.me.id;

  // Use our shared team selection hook
  const {
    teamId: selectedTeamId,
    teamName: selectedTeamName,
    selectTeam: selectTeamBase,
    clearTeamSelection: clearTeamSelectionBase
  } = useTeamSelection({
    userId,
    initialTeamContext: props.me.teamContext,
  });

  // State for handling team selection dropdown
  const [showTeamsList, setShowTeamsList] = useState(false);

  // Handle URL 'team' param for backward compatibility
  // Skip on /knowledge/tierzero.md where team param is used for instruction editing
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const teamId = searchParams.get("team");
    const demoParam = searchParams.get("demo");
    let shouldReplace = false;

    // Don't process team param on the knowledge agent page - it's used for team instruction editing there
    const isKnowledgeAgentPage = location.pathname === knowledgeBasePath || location.pathname === "/knowledge/agent.md";

    if (teamId && !isKnowledgeAgentPage) {
      // If URL contains team parameter, use it in state (assuming no team name available from URL)
      selectTeamBase(teamId, "Team from URL");

      // But remove it from URL for cleaner URLs going forward
      searchParams.delete("team");
      shouldReplace = true;
    }

    if (demoParam === "1") {
      localStorage.setItem("tz_demo_app", "true");
      setIsDemoApp(true);
      searchParams.delete("demo");
      shouldReplace = true;
    } else if (demoParam === "0") {
      localStorage.removeItem("tz_demo_app");
      setIsDemoApp(false);
      searchParams.delete("demo");
      shouldReplace = true;
    }

    if (shouldReplace) {
      navigate({
        pathname: location.pathname,
        search: searchParams.toString() === "" ? "" : searchParams.toString()
      }, { replace: true }); // Use replace to avoid adding to history stack
    }
  }, [location.search, navigate, location.pathname, selectTeamBase]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setIsDemoApp(localStorage.getItem("tz_demo_app") === "true");
  }, []);

  // Wrapper for selectTeam to close dropdown and reload page
  const selectTeam = useCallback(async (teamId: string, teamName: string) => {
    setIsDropdownOpen(false);

    // Wait for the mutation to complete before reloading
    const success = await selectTeamBase(teamId, teamName);
    if (success) {
      // Reload the page to ensure the new team is reflected in queries
      window.location.reload();
    }
  }, [selectTeamBase]);

  // Wrapper for clearTeamSelection to close dropdown (also clears org for super admins)
  const clearTeamSelection = useCallback(() => {
    clearTeamSelectionBase();

    // For super admins, also clear the organization selection and all related state
    if (isSuperAdmin) {
      localStorage.removeItem("tz_current_org_id");
      localStorage.removeItem("tz_auto_selected_org_id");
      // Clear all team selection entries
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("tz-team:")) {
          localStorage.removeItem(key);
        }
      });
      // Set flag to force org selection modal to show (even for single-org users)
      localStorage.setItem("tz_debug_force_org_selection", "true");
    }

    setIsDropdownOpen(false);
    window.location.reload();
  }, [clearTeamSelectionBase, isSuperAdmin]);

  // Toggle dropdown and load teams data when opened
  const toggleDropdown = useCallback(() => {
    if (!isDropdownOpen && organizationId) {
      setShowTeamsList(true);
    }
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen, organizationId]);


  return (
    <nav className="bg-background border-b border-border relative">

      <div className="h-10 mx-auto flex w-full items-center justify-between px-2 overflow-visible">
        <div className="flex items-center gap-2.5">
          {/* Sidebar toggle */}
          <button
            onClick={toggleLeftNav}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent focus:outline-none transition-colors ml-1.5"
            aria-label="Toggle navigation"
          >
            {isLeftNavCollapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          {/* Vertical divider */}
          <div className="h-5 w-px bg-border" />
          <ProductLogo
            brandTheme={brandTheme}
            tierzeroClassName="h-4 w-auto"
            rootlyClassName="h-5 w-auto"
            showPoweredBy={!isDemoApp}
          />
        </div>

        <div className="flex items-center gap-4 flex-shrink-0 ml-2">
          {/* Team Dropdown */}
          <div className="relative">
            <button
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-md text-[13px] transition-all",
                "bg-transparent hover:bg-accent/50",
                isDropdownOpen && "bg-accent/50"
              )}
              onClick={toggleDropdown}
            >
              <span className="text-muted-foreground">Team:</span>
              <span className={selectedTeamName ? "text-foreground" : "text-muted-foreground italic"}>
                {selectedTeamName || "Select"}
              </span>
              <ChevronDown size={14} className={cn(
                "text-muted-foreground transition-transform ml-0.5",
                isDropdownOpen && "rotate-180"
              )} />
            </button>

            {/* Dropdown content */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-1.5 w-52 bg-popover border border-border rounded-md shadow-md z-50">
                {organizationId && (
                  <Suspense
                    fallback={
                      <div className="p-2 text-xs text-muted-foreground">
                        Loading teams...
                      </div>
                    }
                  >
                    {showTeamsList && (
                      <TeamsList
                        organizationId={organizationId}
                        organizationName={props.me.organization?.name || ""}
                        selectedTeamId={selectedTeamId}
                        onSelectTeam={selectTeam}
                        onClearSelection={clearTeamSelection}
                        isSuperAdmin={isSuperAdmin}
                      />
                    )}
                  </Suspense>
                )}
              </div>
            )}
          </div>

          {/* User Info - hidden on mobile */}
          <div className="hidden sm:flex flex-col items-end">
            <span className="text-xs text-foreground">
              {props.me.firstName} {props.me.lastName}
            </span>
            <span className="text-xs text-muted-foreground">
              {props.me.email}
            </span>
          </div>

          {/* Avatar - static for Rootly managed orgs (identity is managed upstream
              by Rootly SSO, which would re-authenticate any in-app logout instantly,
              so no sign-out UI is exposed), PropelAuth account page for others. */}
          {isRootlyManaged ? (
            <div className="relative">
              <UserAvatar
                pictureUrl={props.me.pictureUrl}
                firstName={props.me.firstName}
                lastName={props.me.lastName}
              />
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsLoading(true);
                redirectToAccountPage({
                  redirectBackToUrl: window.location.href,
                });
              }}
              className="cursor-pointer relative bg-transparent border-none p-0"
            >
              <UserAvatar
                pictureUrl={props.me.pictureUrl}
                firstName={props.me.firstName}
                lastName={props.me.lastName}
                isLoading={isLoading}
              />
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}
